import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { authAPI, billingAPI, walletAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import UpgradeBanner from '../components/UpgradeBanner';
import {
  User, CreditCard, Zap, Check, Loader2, Save,
  Clock, Bot, Shield, AlertTriangle, ArrowUpRight, Crown,
  Headphones, X as XIcon, Cpu, Sparkles, Wallet, Plus, ArrowDown, ArrowUp,
  ChevronDown, ChevronUp, ArrowRight, ArrowLeftRight, Info
} from 'lucide-react';
import { toast } from 'sonner';

const PLAN_COLORS = {
  free: { accent: 'text-slate-300', bg: 'bg-slate-600/20', border: 'border-slate-500/30' },
  standard: { accent: 'text-emerald-300', bg: 'bg-emerald-600/20', border: 'border-emerald-500/30' },
  premium: { accent: 'text-amber-300', bg: 'bg-amber-600/20', border: 'border-amber-500/30' },
  enterprise: { accent: 'text-amber-300', bg: 'bg-amber-600/20', border: 'border-amber-500/30' },
};
const PLAN_ICONS = { free: Zap, standard: Bot, premium: Cpu, enterprise: Crown };
const PLAN_HIERARCHY = { free: 0, standard: 1, premium: 2, enterprise: 3 };

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [profileForm, setProfileForm] = useState({ name: '', company: '', phone: '' });
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [razorpayKeyId, setRazorpayKeyId] = useState('');
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletTxns, setWalletTxns] = useState([]);
  const [addAmount, setAddAmount] = useState('');
  const [addingFunds, setAddingFunds] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState({});

  // Plan change modal state
  const [planChangeModal, setPlanChangeModal] = useState(null); // null or { targetPlanId, preview, loading }
  const [changingPlan, setChangingPlan] = useState(false);

  const defaultTab = searchParams.get('tab') || 'profile';

  const loadData = useCallback(async () => {
    try {
      const [pRes, uRes, kRes] = await Promise.all([billingAPI.plans(), billingAPI.usage(), billingAPI.razorpayKey()]);
      setPlans(pRes.data);
      setUsage(uRes.data);
      setRazorpayKeyId(kRes.data?.key_id || '');
      setWalletBalance(uRes.data?.wallet_balance || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadWallet = async () => {
    try {
      const [bRes, tRes] = await Promise.all([walletAPI.balance(), walletAPI.transactions()]);
      setWalletBalance(bRes.data?.balance || 0);
      setWalletTxns(tRes.data?.transactions || []);
    } catch {}
  };

  useEffect(() => {
    if (user) setProfileForm({ name: user.name || '', company: user.company || '', phone: user.phone || '' });
    loadData();
  }, [user, loadData]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await authAPI.updateProfile(profileForm);
      updateUser(res.data);
      toast.success('Profile updated');
    } catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  // ===== PLAN CHANGE FLOW =====
  const handlePlanChangeClick = async (targetPlanId) => {
    // Open modal with preview
    setPlanChangeModal({ targetPlanId, preview: null, loading: true });
    try {
      const res = await billingAPI.planChangePreview(targetPlanId);
      setPlanChangeModal({ targetPlanId, preview: res.data, loading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load plan change details');
      setPlanChangeModal(null);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleConfirmPlanChange = async () => {
    if (!planChangeModal?.preview) return;
    const { targetPlanId, preview } = planChangeModal;
    setChangingPlan(true);

    try {
      if (preview.is_upgrade && preview.payment_required > 0) {
        // UPGRADE: Need payment first
        const orderRes = await billingAPI.createUpgradeOrder(targetPlanId);

        if (orderRes.data.demo_mode) {
          // Demo mode — auto-process payment + change plan
          const res = await billingAPI.changePlan({ target_plan_id: targetPlanId });
          toast.success(res.data.message);
          if (res.data.converted_minutes > 0) {
            toast.info(`+${res.data.converted_minutes} bonus minutes from your previous plan`, { duration: 5000 });
          }
          if (res.data.converted_wallet_credit > 0) {
            toast.info(`₹${res.data.converted_wallet_credit} credited to wallet from previous plan`, { duration: 5000 });
          }
          setPlanChangeModal(null);
          setChangingPlan(false);
          await loadData();
          await loadWallet();
          return;
        }

        // Real Razorpay payment
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
          toast.error('Payment gateway failed to load');
          setChangingPlan(false);
          return;
        }

        const keyId = orderRes.data.key_id || razorpayKeyId;
        const rzp = new window.Razorpay({
          key: keyId,
          amount: orderRes.data.amount,
          currency: orderRes.data.currency || 'INR',
          order_id: orderRes.data.id,
          name: 'DotCall',
          description: `${preview.is_upgrade ? 'Upgrade' : 'Switch'} to ${preview.target_plan.name} Plan`,
          handler: async (response) => {
            try {
              const res = await billingAPI.changePlan({
                target_plan_id: targetPlanId,
                payment_data: {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature
                }
              });
              toast.success(res.data.message);
              if (res.data.converted_minutes > 0) {
                toast.info(`+${res.data.converted_minutes} bonus minutes from your previous plan`, { duration: 5000 });
              }
              if (res.data.converted_wallet_credit > 0) {
                toast.info(`₹${res.data.converted_wallet_credit} credited to wallet from previous plan`, { duration: 5000 });
              }
              setPlanChangeModal(null);
              await loadData();
              await loadWallet();
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Plan change failed after payment');
            }
            setChangingPlan(false);
          },
          modal: { ondismiss: () => { setChangingPlan(false); } },
          prefill: { name: user?.name, email: user?.email, contact: user?.phone },
          theme: { color: '#F59E0B' },
        });
        rzp.open();
      } else {
        // DOWNGRADE or no-payment change
        const res = await billingAPI.changePlan({ target_plan_id: targetPlanId });
        toast.success(res.data.message);
        if (res.data.converted_minutes > 0) {
          toast.info(`+${res.data.converted_minutes} bonus minutes from your previous plan`, { duration: 5000 });
        }
        setPlanChangeModal(null);
        setChangingPlan(false);
        await loadData();
        await loadWallet();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Plan change failed');
      setChangingPlan(false);
    }
  };

  const handleAddFunds = async () => {
    const amt = parseInt(addAmount);
    if (!amt || amt < 1) { toast.error('Enter a valid amount'); return; }
    setAddingFunds(true);
    try {
      const res = await walletAPI.addFunds(amt);
      if (res.data.demo_mode) {
        toast.success(`₹${amt} added to wallet (demo mode)`);
        setAddAmount('');
        await loadData();
        await loadWallet();
      } else {
        toast.info('Razorpay checkout would open here for real payments');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add funds');
    }
    setAddingFunds(false);
  };

  const minutePercent = usage?.limit_minutes ? Math.min(100, (usage.usage_minutes / usage.limit_minutes) * 100) : 0;
  const isOverLimit = usage && usage.limit_minutes > 0 && usage.usage_minutes > usage.limit_minutes;
  const minutesRemaining = usage && usage.limit_minutes > 0 ? Math.max(0, usage.limit_minutes - usage.usage_minutes) : 0;
  const isEnterprise = usage?.plan?.id === 'enterprise';
  const isPremium = usage?.plan?.id === 'premium';
  const isStandard = usage?.plan?.id === 'standard';
  const showWallet = isStandard || isPremium || isEnterprise;
  const plan = usage?.plan || {};
  const currentPlanId = usage?.plan?.id || 'free';
  const currentRank = PLAN_HIERARCHY[currentPlanId] || 0;
  const minRefill = isEnterprise ? (walletBalance === 0 ? (plan.min_first_refill || 6999) : (plan.min_refill || 1999)) : 500;

  // Helper: get button label and state for a plan card
  const getPlanAction = (p) => {
    const isActive = currentPlanId === p.id;
    const targetRank = PLAN_HIERARCHY[p.id] || 0;

    if (isActive) return { label: 'Current Plan', disabled: true, type: 'current' };
    if (p.id === 'free' && currentRank > 0) return { label: 'Downgrade', disabled: true, type: 'blocked' };
    if (targetRank > currentRank) return { label: 'Upgrade', disabled: false, type: 'upgrade' };
    if (targetRank < currentRank) return { label: 'Downgrade', disabled: false, type: 'downgrade' };
    return { label: 'Switch', disabled: false, type: 'switch' };
  };

  return (
    <div className="max-w-5xl" data-testid="settings-page">
      <h1 className="text-3xl font-bold font-['Outfit'] text-white mb-2">Settings</h1>
      <p className="text-slate-400 mb-8">Manage your account, plan, wallet, and usage</p>

      <UpgradeBanner />

      <Tabs defaultValue={defaultTab} className="space-y-6">
        <TabsList className="bg-slate-900/50 border border-white/5 p-1">
          <TabsTrigger value="profile" className="data-[state=active]:bg-amber-600/20 data-[state=active]:text-amber-300 text-slate-400" data-testid="tab-profile">
            <User className="w-4 h-4 mr-2" /> Profile
          </TabsTrigger>
          <TabsTrigger value="billing" className="data-[state=active]:bg-amber-600/20 data-[state=active]:text-amber-300 text-slate-400" data-testid="tab-billing">
            <CreditCard className="w-4 h-4 mr-2" /> Plans
          </TabsTrigger>
          <TabsTrigger value="wallet" className="data-[state=active]:bg-amber-600/20 data-[state=active]:text-amber-300 text-slate-400" data-testid="tab-wallet"
            onClick={() => loadWallet()}>
            <Wallet className="w-4 h-4 mr-2" /> Wallet
          </TabsTrigger>
          <TabsTrigger value="usage" className="data-[state=active]:bg-amber-600/20 data-[state=active]:text-amber-300 text-slate-400" data-testid="tab-usage">
            <Zap className="w-4 h-4 mr-2" /> Usage
          </TabsTrigger>
        </TabsList>

        {/* Profile */}
        <TabsContent value="profile">
          <div className="glass-card rounded-xl p-6 space-y-5" data-testid="profile-section">
            <h3 className="text-lg font-semibold font-['Outfit'] text-white">Profile Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="text-slate-300 text-sm mb-2 block">Full Name</Label>
                <Input value={profileForm.name} onChange={(e) => setProfileForm(p => ({...p, name: e.target.value}))}
                  data-testid="profile-name-input" className="bg-slate-950 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300 text-sm mb-2 block">Email</Label>
                <Input value={user?.email || ''} disabled className="bg-slate-950/50 border-white/5 text-slate-500" /></div>
              <div><Label className="text-slate-300 text-sm mb-2 block">Company</Label>
                <Input value={profileForm.company} onChange={(e) => setProfileForm(p => ({...p, company: e.target.value}))}
                  data-testid="profile-company-input" className="bg-slate-950 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300 text-sm mb-2 block">Phone</Label>
                <Input value={profileForm.phone} onChange={(e) => setProfileForm(p => ({...p, phone: e.target.value}))}
                  data-testid="profile-phone-input" className="bg-slate-950 border-white/10 text-white" placeholder="+91..." /></div>
            </div>
            <Button onClick={handleSaveProfile} disabled={saving} data-testid="save-profile-button" className="bg-amber-600 hover:bg-amber-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Changes
            </Button>
          </div>
        </TabsContent>

        {/* Plans */}
        <TabsContent value="billing">
          <div className="space-y-6" data-testid="billing-section">
            <div className="glass-card rounded-xl p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold font-['Outfit'] text-white">Current Plan</h3>
                    <Badge className={`${PLAN_COLORS[currentPlanId]?.bg || 'bg-slate-600/20'} ${PLAN_COLORS[currentPlanId]?.accent || 'text-slate-300'} border-0 capitalize`}
                      data-testid="current-plan-badge">{usage?.plan?.name || 'Free'}</Badge>
                  </div>
                  <p className="text-sm text-slate-400">
                    {isEnterprise ? 'Pay as you go — usage from wallet' : !razorpayKeyId ? <span className="text-amber-400/80">Demo mode</span> : 'Payments via Razorpay'}
                  </p>
                </div>
                <div className="text-right">
                  {!isEnterprise && <><div className="text-sm text-slate-400">Minutes remaining</div>
                    <div className={`text-xl font-bold font-['Outfit'] ${minutesRemaining <= 0 ? 'text-red-400' : 'text-white'}`}>
                      {minutesRemaining} / {usage?.limit_minutes || 0}
                      {usage?.bonus_minutes > 0 && (
                        <span className="text-xs text-emerald-400 font-normal ml-1">(incl. +{usage.bonus_minutes} bonus)</span>
                      )}
                    </div></>}
                  {isEnterprise && <><div className="text-sm text-slate-400">Wallet Balance</div>
                    <div className="text-xl font-bold font-['Outfit'] text-amber-300">₹{walletBalance.toLocaleString('en-IN')}</div></>}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {plans.filter(p => p.id !== 'free').map((p, i) => {
                const isActive = currentPlanId === p.id;
                const colors = PLAN_COLORS[p.id] || PLAN_COLORS.free;
                const Icon = PLAN_ICONS[p.id] || Zap;
                const isEnt = p.id === 'enterprise';
                const isPrem = p.id === 'premium';
                const action = getPlanAction(p);

                return (
                  <div key={p.id} className={`relative glass-card rounded-xl p-5 transition-all animate-fade-in flex flex-col ${isActive ? `${colors.border} border shadow-lg` : ''} ${isPrem ? 'ring-1 ring-amber-500/30' : ''}`}
                    style={{animationDelay: `${i*60}ms`}} data-testid={`plan-card-${p.id}`}>
                    {isPrem && <div className="absolute -top-2.5 right-4"><Badge className="bg-amber-600 text-white border-0 text-[10px] px-2 py-0.5">Popular</Badge></div>}

                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 ${colors.accent}`} />
                      <h4 className="text-white font-semibold font-['Outfit']">{p.name}</h4>
                      {isActive && <Badge className="bg-emerald-600/20 text-emerald-300 border-0 text-[10px] ml-auto">Active</Badge>}
                    </div>
                    <Badge className="text-[10px] w-fit mb-3 bg-white/5 text-slate-400 border-white/10">
                      <Sparkles className="w-2.5 h-2.5 mr-1" /> DotCall Voice
                    </Badge>

                    <div className="mb-3">
                      {isEnt ? (
                        <div className="text-lg font-bold font-['Outfit'] text-amber-300">{p.display_price}</div>
                      ) : (
                        <><span className="text-2xl font-bold font-['Outfit'] text-white">{p.price === 0 ? 'Free' : `₹${p.price.toLocaleString('en-IN')}`}</span>
                        {p.price > 0 && <span className="text-sm text-slate-500">/mo</span>}</>
                      )}
                    </div>

                    <div className="space-y-1.5 mb-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-300">
                        <Bot className={`w-3.5 h-3.5 ${colors.accent}`} />{p.agents === -1 ? 'Unlimited' : p.agents} Agent{p.agents !== 1 ? 's' : ''}
                      </div>
                      <div className="flex items-center gap-2 text-slate-300">
                        <Headphones className={`w-3.5 h-3.5 ${colors.accent}`} />
                        {isEnt ? 'Pay as you go' : `${p.minutes} min ${p.id === 'free' ? '(lifetime)' : '/month'}`}
                      </div>
                      {p.display_rate && (
                        <div className="flex items-center gap-2 text-slate-300">
                          <Wallet className={`w-3.5 h-3.5 ${colors.accent}`} />
                          Calls from ₹{p.display_rate}/min
                        </div>
                      )}
                      {p.hard_stop && <div className="flex items-center gap-2 text-red-300/80">
                        <XIcon className="w-3.5 h-3.5 text-red-400" />No extra usage
                      </div>}
                    </div>

                    {p.rate_breakdown && (
                      <div className="mb-3">
                        <button onClick={() => setShowBreakdown(prev => ({...prev, [p.id]: !prev[p.id]}))}
                          className="w-full flex items-center justify-between text-[10px] py-1.5 px-2 rounded-lg transition-colors text-amber-400 hover:bg-amber-600/10"
                          data-testid={`toggle-breakdown-${p.id}`}>
                          <span>Show In-depth Call Charges Details</span>
                          {showBreakdown[p.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                        {showBreakdown[p.id] && (
                          <div className="mt-1.5 p-2.5 rounded-lg bg-white/[0.03] border border-white/5 text-[11px] text-slate-500 animate-fade-in" data-testid={`breakdown-${p.id}`}>
                            {p.rate_breakdown.stt !== undefined && <div className="flex justify-between"><span>STT (Speech-to-Text)</span><span className="text-slate-300">₹{p.rate_breakdown.stt}/min</span></div>}
                            {p.rate_breakdown.tts !== undefined && <div className="flex justify-between"><span>TTS (Text-to-Speech)</span><span className="text-slate-300">₹{p.rate_breakdown.tts}/min</span></div>}
                            {p.rate_breakdown.llm !== undefined && <div className="flex justify-between"><span>Voice AI</span><span className="text-slate-300">₹{p.rate_breakdown.llm}/min</span></div>}
                            {p.rate_breakdown.calling !== undefined && <div className="flex justify-between"><span>Calling</span><span className="text-slate-300">₹{p.rate_breakdown.calling}/min</span></div>}
                            {/* Legacy keys kept for backward compatibility */}
                            {p.rate_breakdown.call !== undefined && <div className="flex justify-between"><span>Voice Call</span><span className="text-slate-300">₹{p.rate_breakdown.call}/min</span></div>}
                            {p.rate_breakdown.stt_tts !== undefined && <div className="flex justify-between"><span>STT + TTS</span><span className="text-slate-300">₹{p.rate_breakdown.stt_tts}/min</span></div>}
                            {p.rate_breakdown.smart_ai !== undefined && (
                              <div className="flex justify-between"><span>Smart AI 100% Human</span><span className="text-slate-300">₹{p.rate_breakdown.smart_ai}/min</span></div>
                            )}
                            <div className="flex justify-between font-medium border-t border-white/5 pt-1 mt-1 text-amber-300">
                              <span>Total (actual per min)</span><span>₹{p.rate_breakdown.total}/min</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mt-auto">
                      <Button
                        onClick={() => !action.disabled && handlePlanChangeClick(p.id)}
                        disabled={action.disabled}
                        data-testid={`plan-action-${p.id}`}
                        className={`w-full text-sm ${
                          action.type === 'current' ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                          : action.type === 'blocked' ? 'bg-slate-800/50 text-slate-600 cursor-not-allowed opacity-50'
                          : action.type === 'upgrade' ? (isPrem ? 'bg-amber-600 hover:bg-amber-700 text-white' : isEnt ? 'bg-amber-600/80 hover:bg-amber-600 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white')
                          : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
                        }`}>
                        {action.type === 'upgrade' && <ArrowUp className="w-3.5 h-3.5 mr-1.5" />}
                        {action.type === 'downgrade' && <ArrowDown className="w-3.5 h-3.5 mr-1.5" />}
                        {action.label}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Free plan note for paid users */}
            {currentRank > 0 && (
              <div className="glass-card rounded-xl p-4 flex items-start gap-3 border border-white/5">
                <Info className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-slate-500">
                  Downgrading to the Free plan is not available from paid plans. You can switch between Standard, Premium, and Enterprise plans with automatic minute/wallet conversion.
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Wallet */}
        <TabsContent value="wallet">
          <div className="space-y-6" data-testid="wallet-section">
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold font-['Outfit'] text-white flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-amber-400" /> Wallet Balance
                  </h3>
                  <p className="text-sm text-slate-400 mt-1">
                    {!showWallet ? 'Available for Standard and above plans' : isEnterprise ? 'Pay-as-you-go balance for calls' : 'Used for overage beyond included minutes'}
                  </p>
                </div>
                <div className="text-right">
                  <div className={`text-3xl font-bold font-['Outfit'] ${walletBalance > 0 ? 'text-emerald-400' : 'text-slate-500'}`} data-testid="wallet-balance">
                    ₹{walletBalance.toLocaleString('en-IN')}
                  </div>
                </div>
              </div>

              {showWallet && (
                <div className="flex items-end gap-3 mt-4 pt-4 border-t border-white/5">
                  <div className="flex-1">
                    <Label className="text-slate-400 text-sm mb-2 block">Add Funds (min ₹{minRefill.toLocaleString('en-IN')})</Label>
                    <Input
                      type="number"
                      value={addAmount}
                      onChange={(e) => setAddAmount(e.target.value)}
                      placeholder={`₹${minRefill.toLocaleString('en-IN')}`}
                      min={minRefill}
                      data-testid="wallet-amount-input"
                      className="bg-slate-950 border-white/10 text-white"
                    />
                  </div>
                  <Button onClick={handleAddFunds} disabled={addingFunds || !showWallet}
                    data-testid="wallet-add-funds-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap">
                    {addingFunds ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Add Funds
                  </Button>
                </div>
              )}

              {!showWallet && (
                <div className="mt-4 p-3 rounded-lg bg-slate-950/50 border border-white/5 text-sm text-slate-500">
                  Upgrade to Standard or above to use the wallet system.
                </div>
              )}
            </div>

            {showWallet && plan.rate_breakdown && (
              <div className="glass-card rounded-xl p-5">
                <button
                  onClick={() => setShowBreakdown(prev => ({...prev, wallet: !prev.wallet}))}
                  className="w-full flex items-center justify-between text-sm text-amber-400"
                  data-testid="toggle-breakdown-wallet"
                >
                  <span className="font-semibold">Show In-depth Call Charges Details</span>
                  {showBreakdown.wallet ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showBreakdown.wallet && (
                  <div className="mt-4 space-y-1.5 text-sm animate-fade-in" data-testid="breakdown-wallet">
                    <p className="text-slate-400 mb-2">
                      {isEnterprise ? 'Every minute is charged from your wallet:' : 'Beyond included minutes, each minute costs:'}
                    </p>
                    <div className="grid grid-cols-2 gap-2 max-w-xs">
                      {plan.rate_breakdown.stt !== undefined && <><span className="text-slate-500">STT (Speech-to-Text)</span><span className="text-white text-right">₹{plan.rate_breakdown.stt}/min</span></>}
                      {plan.rate_breakdown.tts !== undefined && <><span className="text-slate-500">TTS (Text-to-Speech)</span><span className="text-white text-right">₹{plan.rate_breakdown.tts}/min</span></>}
                      {plan.rate_breakdown.llm !== undefined && <><span className="text-slate-500">Voice AI</span><span className="text-white text-right">₹{plan.rate_breakdown.llm}/min</span></>}
                      {plan.rate_breakdown.calling !== undefined && <><span className="text-slate-500">Calling</span><span className="text-white text-right">₹{plan.rate_breakdown.calling}/min</span></>}
                      {/* Legacy keys */}
                      {plan.rate_breakdown.call !== undefined && <><span className="text-slate-500">Voice Call</span><span className="text-white text-right">₹{plan.rate_breakdown.call}/min</span></>}
                      {plan.rate_breakdown.stt_tts !== undefined && <><span className="text-slate-500">STT + TTS</span><span className="text-white text-right">₹{plan.rate_breakdown.stt_tts}/min</span></>}
                      {plan.rate_breakdown.smart_ai !== undefined && (
                        <><span className="text-slate-500">Smart AI 100% Human</span><span className="text-white text-right">₹{plan.rate_breakdown.smart_ai}/min</span></>
                      )}
                      <span className="font-medium border-t border-white/5 pt-1 text-amber-300">Total (actual per min)</span>
                      <span className="font-bold text-right border-t border-white/5 pt-1 text-amber-300">₹{plan.rate_breakdown.total}/min</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {walletTxns.length > 0 && (
              <div className="glass-card rounded-xl p-5">
                <h4 className="text-sm font-semibold text-white mb-3">Recent Transactions</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {[...walletTxns].reverse().map((t) => (
                    <div key={t.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-2">
                        {t.type === 'credit' ? <ArrowDown className="w-4 h-4 text-emerald-400" /> : <ArrowUp className="w-4 h-4 text-red-400" />}
                        <div>
                          <div className="text-sm text-white">{t.description}</div>
                          <div className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</div>
                        </div>
                      </div>
                      <span className={`text-sm font-medium ${t.type === 'credit' ? 'text-emerald-400' : 'text-red-400'}`}>
                        {t.type === 'credit' ? '+' : '-'}₹{t.amount.toLocaleString('en-IN')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Usage */}
        <TabsContent value="usage">
          <div className="space-y-6" data-testid="usage-section">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="glass-card rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3"><Clock className="w-4 h-4 text-amber-400" /><span className="text-sm text-slate-400">Minutes Used</span></div>
                <div className="text-2xl font-bold font-['Outfit'] text-white mb-2">
                  {usage?.usage_minutes || 0}
                  {!isEnterprise && <span className="text-sm text-slate-500 font-normal ml-1">/ {usage?.limit_minutes || 0} included</span>}
                  {isEnterprise && <span className="text-sm text-slate-500 font-normal ml-1">total</span>}
                </div>
                {!isEnterprise && <Progress value={Math.min(minutePercent, 100)}
                  className={`h-2 ${isOverLimit ? '[&>div]:bg-red-500' : minutePercent > 80 ? '[&>div]:bg-amber-500' : '[&>div]:bg-amber-500'}`} data-testid="minutes-progress" />}
                {usage?.bonus_minutes > 0 && !isEnterprise && (
                  <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1"><Check className="w-3 h-3" />Includes +{usage.bonus_minutes} bonus min from plan conversion</p>
                )}
                {usage?.hard_stop && usage?.usage_minutes >= usage?.limit_minutes && (
                  <p className="text-xs text-red-400 mt-2 flex items-center gap-1" data-testid="hard-stop-warning"><AlertTriangle className="w-3 h-3" />Limit reached — upgrade to continue</p>)}
              </div>

              <div className="glass-card rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3"><Bot className="w-4 h-4 text-amber-400" /><span className="text-sm text-slate-400">AI Agents</span></div>
                <div className="text-2xl font-bold font-['Outfit'] text-white mb-2">
                  {usage?.agent_count || 0}<span className="text-sm text-slate-500 font-normal ml-1">/ {usage?.agent_limit === -1 ? 'Unlimited' : usage?.agent_limit || 1}</span>
                </div>
                <Progress value={usage?.agent_limit === -1 ? 5 : usage?.agent_limit ? ((usage.agent_count || 0) / usage.agent_limit) * 100 : 0} className="h-2 [&>div]:bg-blue-500" />
              </div>

              <div className="glass-card rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3"><Wallet className="w-4 h-4 text-amber-400" /><span className="text-sm text-slate-400">{isEnterprise ? 'Wallet Spent' : 'Overage Cost'}</span></div>
                <div className="text-2xl font-bold font-['Outfit'] text-white mb-1">₹{(usage?.overage_cost || 0).toLocaleString('en-IN')}</div>
                <p className="text-xs text-slate-500">{usage?.overage_minutes || 0} {isEnterprise ? 'total' : 'extra'} min @ ₹{usage?.overage_rate || 0}/min</p>
              </div>
            </div>

            <div className="glass-card rounded-xl p-5">
              <h3 className="text-base font-semibold font-['Outfit'] text-white mb-4">Usage Breakdown</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><div className="text-xs text-slate-500 mb-1">Total Calls</div><div className="text-lg font-semibold text-white">{usage?.usage_calls || 0}</div></div>
                <div><div className="text-xs text-slate-500 mb-1">Total Minutes</div><div className="text-lg font-semibold text-white">{usage?.usage_minutes || 0}</div></div>
                <div><div className="text-xs text-slate-500 mb-1">{isEnterprise ? 'Rate/min' : 'Included Min'}</div><div className="text-lg font-semibold text-white">{isEnterprise ? `₹${usage?.overage_rate || 34}` : usage?.limit_minutes || 0}</div></div>
                <div><div className="text-xs text-slate-500 mb-1">Wallet Balance</div><div className="text-lg font-semibold text-emerald-400">₹{walletBalance.toLocaleString('en-IN')}</div></div>
              </div>
            </div>

            <div className="glass-card rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-amber-400" /><span className="text-sm text-white font-medium">How billing works</span></div>
              <div className="text-sm text-slate-400 space-y-1">
                {usage?.hard_stop ? (
                  <p>Your <span className="text-white font-medium">Free plan</span> includes {usage?.limit_minutes} minutes (lifetime). After that, calls are blocked. Upgrade to continue.</p>
                ) : isEnterprise ? (
                  <p>Your <span className="text-amber-300 font-medium">Enterprise plan</span> charges ₹{usage?.overage_rate}/min from your wallet for every call. Add funds to keep calling.</p>
                ) : (
                  <p>Your <span className="text-amber-300 font-medium">{usage?.plan?.name} plan</span> includes {usage?.limit_minutes} min/month. Beyond that, ₹{usage?.overage_rate}/min is deducted from your wallet.</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* ===== PLAN CHANGE MODAL ===== */}
      {planChangeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !changingPlan && setPlanChangeModal(null)}>
          <div className="w-full max-w-lg bg-[#0c1222] border border-white/10 rounded-2xl shadow-2xl p-6 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            {planChangeModal.loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-amber-400 animate-spin mb-3" />
                <p className="text-slate-400 text-sm">Calculating plan change...</p>
              </div>
            ) : planChangeModal.preview ? (
              <PlanChangePreviewContent
                preview={planChangeModal.preview}
                onConfirm={handleConfirmPlanChange}
                onCancel={() => setPlanChangeModal(null)}
                loading={changingPlan}
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Plan Change Preview Modal Content =====
function PlanChangePreviewContent({ preview, onConfirm, onCancel, loading }) {
  const { current_plan, target_plan, is_upgrade, remaining_minutes, remaining_value_rupees,
    wallet_balance, converted_minutes, converted_wallet_credit, payment_required,
    target_total_minutes, target_wallet_after } = preview;

  const fromColor = PLAN_COLORS[current_plan.id] || PLAN_COLORS.free;
  const toColor = PLAN_COLORS[target_plan.id] || PLAN_COLORS.free;
  const FromIcon = PLAN_ICONS[current_plan.id] || Zap;
  const ToIcon = PLAN_ICONS[target_plan.id] || Zap;
  const isToEnterprise = target_plan.id === 'enterprise';
  const isFromEnterprise = current_plan.id === 'enterprise';

  return (
    <>
      <div className="flex items-center gap-2 mb-5">
        <ArrowLeftRight className={`w-5 h-5 ${is_upgrade ? 'text-emerald-400' : 'text-amber-400'}`} />
        <h3 className="text-lg font-semibold font-['Outfit'] text-white">
          {is_upgrade ? 'Upgrade' : 'Downgrade'} Plan
        </h3>
        <Badge className={`ml-auto text-[10px] ${is_upgrade ? 'bg-emerald-600/20 text-emerald-300' : 'bg-amber-600/20 text-amber-300'} border-0`}>
          {is_upgrade ? 'UPGRADE' : 'DOWNGRADE'}
        </Badge>
      </div>

      {/* Plan comparison */}
      <div className="flex items-center gap-3 mb-5">
        <div className={`flex-1 rounded-xl p-3 ${fromColor.bg} border ${fromColor.border}`}>
          <div className="flex items-center gap-1.5 mb-1">
            <FromIcon className={`w-3.5 h-3.5 ${fromColor.accent}`} />
            <span className={`text-sm font-medium ${fromColor.accent}`}>{current_plan.name}</span>
          </div>
          <div className="text-xs text-slate-400">₹{current_plan.per_min_rate}/min</div>
        </div>
        <ArrowRight className="w-5 h-5 text-slate-500 flex-shrink-0" />
        <div className={`flex-1 rounded-xl p-3 ${toColor.bg} border ${toColor.border}`}>
          <div className="flex items-center gap-1.5 mb-1">
            <ToIcon className={`w-3.5 h-3.5 ${toColor.accent}`} />
            <span className={`text-sm font-medium ${toColor.accent}`}>{target_plan.name}</span>
          </div>
          <div className="text-xs text-slate-400">₹{target_plan.per_min_rate}/min</div>
        </div>
      </div>

      {/* Conversion details */}
      <div className="space-y-3 mb-5">
        <div className="rounded-xl bg-white/[0.03] border border-white/5 p-4 space-y-2.5">
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Conversion Details</h4>

          {/* From enterprise: wallet balance → minutes */}
          {isFromEnterprise && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Current wallet balance</span>
                <span className="text-white font-medium">₹{wallet_balance.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Converted to {target_plan.name} minutes</span>
                <span className="text-emerald-400 font-medium">
                  ₹{wallet_balance.toLocaleString('en-IN')} ÷ ₹{target_plan.per_min_rate}/min = {converted_minutes} min
                </span>
              </div>
            </>
          )}

          {/* From standard/premium: remaining minutes → target minutes or wallet */}
          {!isFromEnterprise && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Remaining {current_plan.name} minutes</span>
                <span className="text-white font-medium">{remaining_minutes} min</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Value at ₹{current_plan.per_min_rate}/min</span>
                <span className="text-white font-medium">₹{remaining_value_rupees.toLocaleString('en-IN')}</span>
              </div>
              {isToEnterprise ? (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Credited to Enterprise wallet</span>
                  <span className="text-emerald-400 font-medium">+₹{converted_wallet_credit.toLocaleString('en-IN')}</span>
                </div>
              ) : (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Converted to {target_plan.name} minutes</span>
                  <span className="text-emerald-400 font-medium">
                    {remaining_minutes} × (₹{current_plan.per_min_rate}/₹{target_plan.per_min_rate}) = {converted_minutes} min
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Payment section for upgrades */}
        {is_upgrade && payment_required > 0 && (
          <div className="rounded-xl bg-amber-600/[0.08] border border-amber-500/20 p-4 space-y-2">
            <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Payment Required</h4>
            <div className="flex justify-between text-sm">
              <span className="text-slate-300">
                {isToEnterprise ? 'Enterprise first refill' : `${target_plan.name} subscription`}
              </span>
              <span className="text-amber-300 font-bold text-lg">₹{payment_required.toLocaleString('en-IN')}</span>
            </div>
            {isToEnterprise && (
              <p className="text-xs text-slate-500">This amount will be added to your Enterprise wallet balance</p>
            )}
          </div>
        )}

        {/* Result summary */}
        <div className="rounded-xl bg-emerald-600/[0.06] border border-emerald-500/20 p-4 space-y-2">
          <h4 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">After {is_upgrade ? 'Upgrade' : 'Downgrade'}</h4>
          {isToEnterprise ? (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-slate-300">Wallet balance</span>
                <span className="text-emerald-400 font-bold">₹{target_wallet_after.toLocaleString('en-IN')}</span>
              </div>
              <p className="text-xs text-slate-500">Calls charged at ₹{target_plan.per_min_rate}/min from wallet</p>
            </>
          ) : (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-slate-300">Plan included minutes</span>
                <span className="text-white">{target_plan.minutes} min/month</span>
              </div>
              {converted_minutes > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-300">Bonus from conversion</span>
                  <span className="text-emerald-400 font-medium">+{converted_minutes} min</span>
                </div>
              )}
              <div className="flex justify-between text-sm border-t border-white/5 pt-2">
                <span className="text-white font-medium">Total available minutes</span>
                <span className="text-emerald-400 font-bold">{target_total_minutes} min</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3">
        <Button onClick={onCancel} disabled={loading} variant="ghost"
          className="flex-1 text-slate-400 hover:text-white hover:bg-white/5 border border-white/10">
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={loading}
          className={`flex-1 text-white ${is_upgrade ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}`}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {is_upgrade
            ? (payment_required > 0 ? `Pay ₹${payment_required.toLocaleString('en-IN')} & Upgrade` : 'Confirm Upgrade')
            : 'Confirm Downgrade'
          }
        </Button>
      </div>
    </>
  );
}
